import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { GhostWatchClient, isApiError } from "@ghostwatch/sdk";

/**
 * The API base. Same-origin by default so the console can be served by the control
 * plane (or any static host in front of it) without rebuilding per environment;
 * `VITE_GHOSTWATCH_API` overrides it when the two are split across origins.
 */
const API_BASE = (import.meta.env.VITE_GHOSTWATCH_API as string | undefined) ?? "";

/**
 * The session token lives in memory only — deliberately not in localStorage or a
 * cookie readable by script. A monitoring console shows every asset a tenant owns,
 * so a token persisted where any XSS could read it is worth far more to an attacker
 * than the convenience of surviving a page refresh. Refresh means log in again.
 */
interface SessionState {
  client: GhostWatchClient;
  token: string | null;
  email: string | null;
  workspaceId: string | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  const client = useMemo(() => new GhostWatchClient(API_BASE, token, workspaceId), [token, workspaceId]);

  const login = useCallback(async (addr: string, password: string) => {
    const anon = new GhostWatchClient(API_BASE);
    const res = await anon.login(addr, password);
    setToken(res.token);
    setEmail(addr);
    // Pick up the first workspace so list calls are scoped from the first render.
    try {
      const scoped = new GhostWatchClient(API_BASE, res.token);
      const { items } = await scoped.listWorkspaces();
      setWorkspaceId(items[0]?.id ?? null);
    } catch {
      // A missing workspace list is not fatal — the API still scopes by tenant.
      setWorkspaceId(null);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.logout();
    } catch {
      // Even if the server call fails, drop local credentials: the user asked to
      // be logged out, and keeping the token because a request failed is worse.
    }
    setToken(null);
    setEmail(null);
    setWorkspaceId(null);
  }, [client]);

  const value = useMemo<SessionState>(
    () => ({ client, token, email, workspaceId, login, logout }),
    [client, token, email, workspaceId, login, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** Turns any thrown value into something safe to render. */
export function errorMessage(err: unknown): string {
  if (isApiError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Unexpected error";
}
